import React from 'react';
import classNames from 'classnames';
import Icon, { Size } from 'components/general/icon/icon';
import LoadingSpinner from "../loading_spinner/LoadingSpinner";

const styles = require("./DocumentUploader.module.scss");

export type DefaultDocument = {
  id: string;
  name: string;
  size?: number;
  standard?: string;
  uploaded_at?: string;
};

type UploaderEmptyStateProps = {
  title: string;
  subtitle?: string;
  disabled?: boolean;
  error?: boolean;
  onClick: () => void;
  onDrop: (e: React.DragEvent) => void;
};

export const UploaderEmptyState: React.FC<UploaderEmptyStateProps> = ({
  title,
  subtitle,
  disabled,
  error,
  onClick,
  onDrop
}) => {
  const wrapperClassName = classNames({
    [styles.dropzone]: true,
    [styles.disabled]: disabled,
    [styles.error]: error,
  });

  return (
    <div
      className={wrapperClassName}
      onClick={disabled ? undefined : onClick}
      onDrop={disabled ? undefined : onDrop}
      onDragOver={e => e.preventDefault()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
    >
      <div className={styles.content}>
        <Icon name="uploadFile" size={Size.MediumBig} color="black" />
        <div className={styles.fileInfo}>
          <span className={styles.title}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      </div>
    </div>
  );
};

type UploaderLoadingStateProps = {
  loadingTitle: string;
};

export const UploaderLoadingState: React.FC<UploaderLoadingStateProps> = ({
  loadingTitle
}) => (
  <div className={styles.loadingWrapper}>
    <div className={styles.content}>
      <LoadingSpinner size="small" />
      <div className={styles.fileInfo}>
        <span className={styles.title}>{loadingTitle}</span>
      </div>
    </div>
  </div>
);

type UploaderSingleModeProps = {
  document: DefaultDocument;
  disabled?: boolean;
  formatFileSize: (bytes?: number) => string;
  onClick: () => void;
  onDrop: (e: React.DragEvent) => void;
};

export const UploaderSingleMode: React.FC<UploaderSingleModeProps> = ({
  document,
  disabled,
  formatFileSize,
  onClick,
  onDrop,
}) => (
  <div
    className={classNames(styles.documentWrapper, styles.singleModeWithFile)}
    onClick={disabled ? undefined : onClick}
    onDrop={disabled ? undefined : onDrop}
    onDragOver={e => e.preventDefault()}
    role="button"
    tabIndex={disabled ? -1 : 0}
    onKeyDown={(e) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    }}
  >
    <div className={styles.content}>
      <Icon name="paperClip" size={Size.MediumBig} color="tuftsBlueMidLight" />
      <div className={classNames(styles.fileInfo, styles.hasFile)}>
        <span className={styles.title}>{document.name}</span>
        <span className={styles.subtitle}>{formatFileSize(document.size)}</span>
      </div>
      
      <Icon name="repeat" size={Size.MediumBig} color="black" />
    </div>
  </div>
);

type UploaderMultipleModeProps = {
  document: DefaultDocument;
  isSelected: boolean;
  formName?: string;
  onSelect: (document: DefaultDocument) => void;
  onReplace: () => void;
};

export const UploaderMultipleMode: React.FC<UploaderMultipleModeProps> = ({
  document,
  isSelected,
  formName,
  onSelect,
  onReplace
}) => {
  const wrapperClassName = classNames(
    styles.documentWrapper,
    { [styles.selected]: isSelected }
  );

  return (
    <div className={wrapperClassName} onClick={() => onSelect(document)}>
    {formName && (
      <input
        type="radio"
        name={formName}
        value={document.id}
        checked={isSelected}
        className={styles.hiddenRadioInput}
        aria-label={`Select ${document.name}`}
      />
    )}
    <div className={styles.content}>
      <Icon 
        name={isSelected ? "radioButtonChecked" : "radioButtonUnchecked"} 
        size={Size.MediumBig}
        color="tuftsBlueBase"
      />
        <div className={classNames(styles.fileInfo, styles.hasFile)}>
        <span className={styles.title}>{document.name}</span>
        <span className={styles.subtitle}>Uploaded now</span>
      </div>
      <button
        className={styles.actionButton}
        onClick={(e) => {
          e.stopPropagation();
          onReplace();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
        aria-label="Replace document"
      >
        <Icon name="repeat" size={Size.MediumBig} color="black" />
      </button>
    </div>
  </div>
);
};
